import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';

const SceneContainer = styled.div`
  width: 100%;
  max-width: 1400px;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  color: #fff;
  padding: 2rem;
`;

const SceneTitle = styled.h2`
  font-size: 3rem;
  font-weight: 300;
  text-transform: uppercase;
  letter-spacing: 4px;
  margin: 0 0 1rem 0;
  text-align: center;
  background: linear-gradient(45deg, #fff, #00ff88);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 0 30px rgba(0, 255, 136, 0.5);
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.3rem;
  color: #00ff88;
  text-align: center;
  margin: 0 0 3rem 0;
  font-weight: 300;
  letter-spacing: 2px;
  opacity: 0.8;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 2rem;
  }
`;

const LibraryPlatform = styled.div`
  width: 700px;
  height: 500px;
  background: linear-gradient(145deg, #1a1a1a, #0a0a0a);
  border: 3px solid #00ff88;
  border-radius: 25px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -10px;
    left: -10px;
    right: -10px;
    bottom: -10px;
    border-radius: 25px;
    background: linear-gradient(45deg, #00ff88, #00cc6a);
    opacity: 0.2;
    filter: blur(20px);
    animation: pulse 4s ease-in-out infinite;
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 0.2; transform: scale(1); }
    50% { opacity: 0.4; transform: scale(1.02); }
  }
  
  @media (max-width: 768px) {
    width: 90%;
    height: 400px;
  }
`;

const BookShelf = styled.div`
  position: absolute;
  width: 80%;
  height: 70%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  padding: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 0.8rem;
    padding: 1.5rem;
  }
`;

const Book = styled.div<{ isActive: boolean; color: string }>`
  width: 100%;
  height: 120px;
  background: ${props => props.color};
  border: 2px solid ${props => props.isActive ? '#00ff88' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 8px;
  position: relative;
  cursor: pointer;
  transition: all 0.4s ease;
  transform-style: preserve-3d;
  
  &:hover {
    transform: translateY(-10px) rotateY(15deg);
    border-color: #00ff88;
    box-shadow: 0 15px 30px rgba(0, 255, 136, 0.3);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 10px;
    left: 10px;
    right: 10px;
    height: 2px;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 1px;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 25px;
    left: 10px;
    right: 10px;
    height: 1px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 0.5px;
  }
  
  .book-title {
    position: absolute;
    bottom: 10px;
    left: 10px;
    right: 10px;
    font-size: 0.7rem;
    color: #fff;
    font-weight: 600;
    text-align: center;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
    opacity: ${props => props.isActive ? 1 : 0.8};
    transition: opacity 0.3s ease;
  }
  
  .book-glow {
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    background: ${props => props.color};
    border-radius: 8px;
    opacity: ${props => props.isActive ? 0.3 : 0};
    filter: blur(10px);
    transition: opacity 0.3s ease;
  }
`;

const CentralEmblem = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  width: 150px;
  height: 150px;
  background: linear-gradient(145deg, #8B4513, #A0522D);
  border: 3px solid #00ff88;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 30px rgba(0, 255, 136, 0.3);
  
  &::before {
    content: '📚';
    font-size: 3rem;
    filter: grayscale(0.3);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -10px;
    left: -10px;
    right: -10px;
    bottom: -10px;
    border-radius: 50%;
    background: linear-gradient(45deg, #00ff88, #00cc6a);
    opacity: 0.2;
    filter: blur(15px);
    animation: pulse 3s ease-in-out infinite;
  }
  
  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
    
    &::before {
      font-size: 2rem;
    }
  }
`;

const CallToAction = styled.div`
  text-align: center;
  margin-top: 2rem;
  
  .cta-title {
    font-size: 2rem;
    color: #00ff88;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 2px;
    margin: 0 0 1rem 0;
    text-shadow: 0 0 20px rgba(0, 255, 136, 0.5);
  }
  
  .cta-subtitle {
    font-size: 1rem;
    color: #ccc;
    margin: 0;
    font-weight: 300;
    letter-spacing: 1px;
    opacity: 0.8;
  }
  
  .cta-line {
    width: 100px;
    height: 2px;
    background: #00ff88;
    margin: 1.5rem auto 0;
    position: relative;
    
    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 4px;
      height: 4px;
      background: #00ff88;
      border-radius: 50%;
    }
  }
  
  @media (max-width: 768px) {
    .cta-title {
      font-size: 1.5rem;
    }
  }
`;

const FloatingWisdom = styled.div`
  position: absolute;
  top: 2rem;
  right: 2rem;
  background: rgba(0, 0, 0, 0.9);
  border: 2px solid #00ff88;
  border-radius: 15px;
  padding: 1.5rem;
  max-width: 300px;
  backdrop-filter: blur(10px);
  
  .wisdom-title {
    font-size: 1rem;
    color: #00ff88;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin: 0 0 1rem 0;
  }
  
  .wisdom-text {
    font-size: 0.9rem;
    color: #ccc;
    line-height: 1.6;
    margin: 0;
    font-style: italic;
  }
  
  @media (max-width: 768px) {
    position: relative;
    top: auto;
    right: auto;
    margin-top: 2rem;
    max-width: 100%;
  }
`;

const LibraryScene: React.FC = () => {
  const sceneRef = useRef<HTMLDivElement>(null);
  const [activeBook, setActiveBook] = useState<number | null>(null);

  const books = [
    { id: 1, title: "Innovation", color: "#FF6B6B" },
    { id: 2, title: "Creativity", color: "#4ECDC4" },
    { id: 3, title: "Technology", color: "#45B7D1" },
    { id: 4, title: "Art", color: "#96CEB4" },
    { id: 5, title: "Science", color: "#FFEAA7" },
    { id: 6, title: "Design", color: "#DDA0DD" },
    { id: 7, title: "Engineering", color: "#F8B500" },
    { id: 8, title: "Philosophy", color: "#A8E6CF" }
  ];

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    // Animate books on scene enter
    gsap.fromTo('.book', 
      { 
        opacity: 0, 
        y: 50,
        scale: 0.8,
        rotationY: -90
      },
      { 
        opacity: 1, 
        y: 0,
        scale: 1,
        rotationY: 0,
        duration: 1.2,
        ease: "power2.out",
        stagger: 0.1
      }
    );

    // Animate central emblem
    gsap.fromTo(CentralEmblem, 
      { 
        scale: 0, 
        opacity: 0,
        rotation: -180
      },
      { 
        scale: 1, 
        opacity: 1,
        rotation: 0,
        duration: 1.5,
        ease: "back.out(1.7)",
        delay: 0.5
      }
    );

  }, []);

  const handleBookClick = (bookId: number) => {
    setActiveBook(activeBook === bookId ? null : bookId);
  };

  return (
    <SceneContainer ref={sceneRef} className="scene">
      <SceneTitle>The Library</SceneTitle>
      <Subtitle>More help finding your gift</Subtitle>
      
      <LibraryPlatform>
        <BookShelf>
          {books.map((book) => (
            <Book
              key={book.id}
              className="book"
              color={book.color}
              isActive={activeBook === book.id}
              onClick={() => handleBookClick(book.id)}
            >
              <div className="book-glow"></div>
              <div className="book-title">{book.title}</div>
            </Book>
          ))}
        </BookShelf>
        
        <CentralEmblem />
      </LibraryPlatform>
      
      <CallToAction>
        <h3 className="cta-title">Origin Stories</h3>
        <p className="cta-subtitle">Find your inspiration</p>
        <div className="cta-line"></div>
      </CallToAction>
      
      <FloatingWisdom>
        <h4 className="wisdom-title">Ancient Wisdom</h4>
        <p className="wisdom-text">
          "Knowledge is the foundation of all great achievements. Seek it in every corner of this sacred space."
        </p>
      </FloatingWisdom>
    </SceneContainer>
  );
};

export default LibraryScene;
